
/**
 * Module dependencies.
 */

import { ChangeEvent, ReactNode, useState } from 'react';
import { FormGroup, textDesktopStyle } from './forms/fields-styled-components';
import { Icon } from 'react-components/media';
import { Text } from './typography';
import { color, media } from 'react-components/styles';
import { ifProp, theme } from 'styled-tools';
import checkedIcon from './assets/svg/forms/radio-checked.svg';
import styled, { css } from 'styled-components';
import uncheckedIcon from './assets/svg/forms/radio.svg';
import uuid from 'uuid/v1';

/**
 * Export `RadioButtonProps` type.
 */

export type RadioButtonProps = {
  checked?: boolean;
  className?: string;
  disabled?: boolean;
  hasError?: boolean;
  helpText?: ReactNode;
  inline?: boolean;
  label: string;
  name: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  value: string | null | undefined;
};

/**
 * `StyledFormGroup` styled component.
 */

const StyledFormGroup = styled(FormGroup)<{ inline: boolean}>`
  ${ifProp('inline', css`
    display: inline-block;
    margin-right: 16px;
  `)}
`;

/**
 * `StyledIcon` styled component.
 */

const StyledIcon = styled(Icon)`
  left: 0;
  max-width: 24px;
  position: absolute;
  top: -2px;
  transition: opacity ${theme('animations.defaultTransition')};
  width: 24px;

  ${media.min('md')`
    top: -1px;
  `}
`;

/**
 * `CheckedIcon` styled component.
 */

const CheckedIcon = styled(StyledIcon).attrs({ icon: checkedIcon })<{ disabled: boolean }>`
  color: ${color('primary')};
  opacity: 0;

  ${ifProp('disabled', css`
    color: ${color('gray300')};
  `)}
`;

/**
 * `UncheckedIcon` styled component.
 */

const UncheckedIcon = styled(StyledIcon).attrs({ icon: uncheckedIcon })<{ disabled: boolean }>`
  color: ${color('gray500')};

  ${ifProp('disabled', css`
    color: ${color('gray300')};
  `)}
`;

/**
 * `Label` styled component.
 */

const Label = styled(Text)<{ disabled: boolean; hasError: boolean }>`
  ${textDesktopStyle}

  color: ${color('secondary')};
  cursor: pointer;
  display: block;
  padding-left: 32px;
  position: relative;

  ${ifProp('hasError', css`
    color: ${color('danger')};
  `)}

  ${ifProp('disabled', css`
    color: ${color('gray400')};
  `)}
`;

/**
 * `Input` styled component.
 */

const Input = styled.input`
  left: 0;
  opacity: 0;
  position: absolute;

  /* stylelint-disable selector-type-no-unknown */
  /* stylelint-disable rule-empty-line-before */
  /* stylelint-disable declaration-block-semicolon-newline-after */
  &:checked + ${Label} ${CheckedIcon} {
    opacity: 1;
  }

  &:focus-visible + ${Label}:not(:hover) {
    ${UncheckedIcon},
    ${CheckedIcon} {
      .radio-icon-stroke-color {
        stroke: ${color('blue200')};
      }
    }
  }

  &:not(:checked) + ${Label}:hover ${UncheckedIcon} {
    color: ${color('gray600')};
  }

  &:checked + ${Label}:hover ${CheckedIcon} {
    .radio-icon-fill-color {
      fill: ${color('blue200')};
    }
  }

  &:checked + ${Label} ${UncheckedIcon},
  &:checked + ${Label}:hover ${UncheckedIcon} {
    opacity: 0;
  }
  /* stylelint-enable selector-type-no-unknown */
  /* stylelint-enable rule-empty-line-before */
  /* stylelint-enable declaration-block-semicolon-newline-after */
`;

/**
 * Export `BaseRadioButton` component.
 */

export function BaseRadioButton(props: RadioButtonProps): JSX.Element {
  const { className, disabled = false, hasError = false, inline = false, label, value, ...rest } = props;
  const [id] = useState(uuid);

  return (
    <StyledFormGroup
      className={className}
      disabled={disabled}
      inline={inline}
    >
      <Input
        {...rest}
        disabled={disabled}
        id={id}
        type={'radio'}
        value={value ?? ''}
      />

      <Label
        as={'label'}
        disabled={disabled}
        hasError={hasError}
        htmlFor={id}
      >
        {label}

        <UncheckedIcon disabled={disabled} />

        <CheckedIcon disabled={disabled} />
      </Label>
    </StyledFormGroup>
  );
}
